import React, { useState } from 'react';
import { Download, Save, FileText, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AGReportTab = ({ selectedAG, token, userRole }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [notes, setNotes] = useState(selectedAG?.compte_rendu || '');
  const [saving, setSaving] = useState(false);

  const handleExportPV = async () => {
    if (!selectedAG?.id) {
      setError('Aucune AG sélectionnée');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Use fetch instead of axios to avoid responseType issues with Emergent scripts
      const response = await fetch(
        `${API}/assemblees/${selectedAG.id}/pv`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('NOT_FOUND');
        }
        throw new Error('DOWNLOAD_ERROR');
      }

      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with AG title and date
      const agDate = selectedAG.date ? new Date(selectedAG.date).toLocaleDateString('fr-FR').replace(/\//g, '-') : 'AG';
      const filename = `PV_${selectedAG.titre || 'AG'}_${agDate}.docx`.replace(/\s+/g, '_');
      link.setAttribute('download', filename);
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccess('Procès-verbal téléchargé avec succès !');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error exporting PV:', err);
      if (err.message === 'NOT_FOUND') {
        setError('Le procès-verbal n\'est pas encore disponible. L\'AG doit être clôturée pour générer le PV.');
      } else {
        setError('Erreur lors de l\'export du procès-verbal. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedAG?.id) return;

    setSaving(true);
    setError(null);

    try {
      await axios.put(
        `${API}/assemblees/${selectedAG.id}`,
        { compte_rendu: notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Notes sauvegardées !');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving notes:', err);
      setError('Erreur lors de la sauvegarde des notes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-[#00D3A8]/10 rounded-xl flex items-center justify-center">
              <FileText className="text-[#00D3A8]" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Procès-Verbal</h2>
              <p className="text-gray-500 text-sm">Export et notes de l'assemblée générale</p>
            </div>
          </div>
          
          {/* Export Button */}
          <button 
            onClick={handleExportPV}
            disabled={loading}
            className="bg-[#00D3A8] hover:bg-[#00C7A1] disabled:bg-gray-300 text-white py-3 px-6 rounded-xl transition-colors flex items-center space-x-2 font-medium"
            data-testid="export-pv-btn"
          >
            {loading ? (
              <>
                <Loader className="animate-spin" size={20} />
                <span>Génération...</span>
              </>
            ) : (
              <>
                <Download size={20} />
                <span>Télécharger le PV (Word)</span>
              </>
            )}
          </button>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="flex items-center space-x-2 bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-4">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}
        
        {success && (
          <div className="flex items-center space-x-2 bg-green-50 border border-green-200 text-green-700 p-4 rounded-lg mb-4">
            <CheckCircle size={20} />
            <span>{success}</span>
          </div>
        )}

        {/* Info about PV */}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
          <p className="text-blue-700 text-sm">
            <strong>Note :</strong> Le procès-verbal au format Word (.docx) contient :
          </p>
          <ul className="text-blue-600 text-sm mt-2 list-disc list-inside space-y-1">
            <li>L'en-tête avec les informations de l'AG et de l'immeuble</li>
            <li>La liste des participants (présents, représentés, absents)</li>
            <li>Le détail de chaque résolution avec les résultats des votes</li>
            <li>Les connexions/déconnexions pendant la séance</li>
            <li>Les zones de signature pour le bureau de l'AG</li>
          </ul>
        </div>
      </div>

      {/* Notes Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Notes complémentaires</h3>
        <p className="text-gray-500 text-sm mb-4">
          Ajoutez des notes ou observations qui seront associées à cette assemblée générale.
        </p>
        
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00D3A8] focus:border-transparent transition-all"
          rows={8}
          placeholder="Rédiger vos notes ou observations concernant cette AG..."
        />

        {userRole === 'gestionnaire' && (
          <div className="flex justify-end mt-4">
            <button 
              onClick={handleSaveNotes}
              disabled={saving}
              className="bg-[#00D3A8] hover:bg-[#00C7A1] disabled:bg-gray-300 text-white py-2 px-6 rounded-lg transition-colors flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  <span>Sauvegarde...</span>
                </>
              ) : (
                <>
                  <Save size={18} />
                  <span>Sauvegarder les notes</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AGReportTab;
